import { ignoreAbortError } from '@/utils/abort-util'
import { StaffingSearchViewModel } from '@/features/admin/staffing/StaffingSearchViewModel'
import type {
  ListWorkspaceMembershipsFn,
  GetCurrentUserWorkspaceMembershipsFn,
} from '@/features/iam'
import { type FilterStorageFactory } from '@/infra/FiltersViewModel'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { action, computed } from 'mobx'
import {
  type StaffingColumnDetails,
  StaffingTableColumns,
} from '@/features/admin/staffing/components/StaffingTable/staffing-table-columns'
import { type TableSortOrder } from '@/components/Table'
import { type WorkspacePosting } from '@/features/admin/staffing/WorkspacePosting'
import { WorkspacePostingItemViewModel } from '@/features/admin/staffing/WorkspacePostingItemViewModel'
import { ViewModelList } from '@/infra/view-model-list'
import { type WorkspacePostingStore } from '@/features/admin/staffing/WorkspacePostingStore'
import { StaffingDetailsViewModel } from '@/features/admin/staffing/components/StaffingDetailsPanel/StaffingDetailsViewModel'
import { task } from 'mobx-task'
import { CreateWorkspacePostingDialogViewModel } from '@/features/admin/staffing/components/CreateWorkspacePostingDialog/CreateWorkspacePostingDialogViewModel'
import { UpdateWorkspacePostingDialogViewModel } from '@/features/admin/staffing/components/UpdateWorkspacePostingDialog/UpdateWorkspacePostingDialogViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * Dependencies of the staffing page.
 */
export interface StaffingViewModelOptions {
  workspacePostingStore: WorkspacePostingStore
  listWorkspaceMemberships: ListWorkspaceMembershipsFn
  getWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn
  createFilterStorage: FilterStorageFactory
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
  closeNav: () => void
}

export class StaffingViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _items = new ViewModelList({
    models: () => this.searcher.filteredItems.slice(),
    create: (workspacePosting: WorkspacePosting) =>
      new WorkspacePostingItemViewModel(
        workspacePosting,
        this.searcher,
        this.staffingDetailsVm,
      ),
  })

  readonly searcher

  /**
   * View model for the staffing details panel.
   */
  readonly staffingDetailsVm

  /**
   * View model for creating a workspace posting.
   */
  readonly createWorkspacePostingVm

  /**
   * View model for updating a workspace posting.
   */
  readonly updateWorkspacePostingVm

  /**
   * Columns to populate the staffing table header and rows with.
   */
  readonly columns = StaffingTableColumns

  constructor(private readonly _options: StaffingViewModelOptions) {
    const {
      workspacePostingStore,
      getWorkspaceMemberships,
      newAlert,
      closeNav,
    } = _options

    this.searcher = new StaffingSearchViewModel(
      workspacePostingStore,
      _options.createAlertRetryWrapper,
      getWorkspaceMemberships,
      _options.createFilterStorage('WorkspacePostings'),
    )

    this.staffingDetailsVm = new StaffingDetailsViewModel(
      workspacePostingStore,
      this.openUpdateWorkspacePostingDialog,
      closeNav,
      newAlert,
      this.refreshStatusFilter,
      _options.createAlertErrorHandler,
      _options.createAlertRetryWrapper,
    )

    this.createWorkspacePostingVm = new CreateWorkspacePostingDialogViewModel(
      workspacePostingStore,
      newAlert,
      _options.createAlertErrorHandler,
      this.refreshStatusFilter,
      getWorkspaceMemberships,
    )

    this.updateWorkspacePostingVm = new UpdateWorkspacePostingDialogViewModel(
      workspacePostingStore,
      newAlert,
      _options.createAlertErrorHandler,
      this.refreshStatusFilter,
      getWorkspaceMemberships,
    )

    this._lifecycle.manage(this._items)
    this._lifecycle.manage(this.searcher)
    this._lifecycle.manage(this.staffingDetailsVm)
  }

  @computed
  get columnIds() {
    return this.columns.map((column) => column.id)
  }

  /**
   * The table rows.
   */
  get items() {
    return this._items.items
  }

  /**
   * Loads the workspace memberships the searcher filters by.
   */
  fetch = task(async () => {
    await this._options.listWorkspaceMemberships({}, this._lifecycle.signal)
  })

  /**
   * Gets the sort order for the specified column.
   *
   * @param column
   */
  getSortOrder(column: StaffingColumnDetails): TableSortOrder {
    return this.searcher.sorting.field === column.field
      ? this.searcher.sorting.direction
      : 'none'
  }

  /**
   * Sets the sort order for the specified column.
   *
   * @param column
   * @param direction
   */
  setSortOrder(column: StaffingColumnDetails, direction: TableSortOrder) {
    if (!column.field) {
      return
    }

    this.searcher.setSortField(column.field)
    this.searcher.setSortDirection(direction)
  }

  /**
   * Refreshes status filter.
   */
  @action.bound
  async refreshStatusFilter() {
    await this.searcher.refreshStatusFilter()
  }

  /**
   * Opens the update workspace posting dialog.
   * @param workspacePosting
   */
  @action.bound
  openUpdateWorkspacePostingDialog(workspacePosting: WorkspacePosting) {
    this.updateWorkspacePostingVm.showWithData(workspacePosting)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetch().catch(ignoreAbortError())
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}
